import React from "react";
import ReactMarkdown from "react-markdown";
import mermaid from "mermaid";
import * as d3 from "d3";
import remarkGfm from "remark-gfm";
import remarktoc from "remark-toc";
import slug from "rehype-slug";
import raw from "rehype-raw";
import { Theme, lighten } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import { Paper, IconButton, Tooltip, Box } from "@mui/material";
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles()((theme: Theme) => ({
  table: {
    padding: theme.spacing(2),
    width: "100%",
    "& img": {
      maxWidth: "100%",
      height: "auto",
    },
  },
  mermaidWrapper: {
    overflow: "hidden",
    position: "relative",
    width: "100%",
    height: "100%",
    "& svg": {
      width: "100%",
      height: "auto",
      transformOrigin: "top left",
    },
  },
  zoomControls: {
    position: "absolute",
    bottom: 12,
    right: 12,
    display: "flex",
    gap: 4,
    zIndex: 10,
  },
  zoomButton: {
    width: 36,
    height: 36,
    padding: 0,
  },
}));

interface MermaidBlockProps {
  chart: string;
}

const MermaidBlock: React.FunctionComponent<MermaidBlockProps> = ({
  chart,
}) => {
  const { classes } = useStyles();
  const [svg, setSvg] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const id = React.useMemo(
    () => `mermaid-${Math.random().toString(36).slice(2, 11)}`,
    [],
  );

  React.useEffect(() => {
    let mounted = true;
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "loose",
      theme: "default",
    });
    mermaid
      .render(id, chart)
      .then(({ svg: renderedSvg }) => {
        if (mounted) {
          setSvg(renderedSvg);
          setError(null);
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err?.message ?? "Unable to render Mermaid diagram");
        }
      });
    return () => {
      mounted = false;
    };
  }, [chart, id]);

  React.useEffect(() => {
    if (svg && containerRef.current) {
      const svgEl = containerRef.current.querySelector("svg");
      if (svgEl) {
        const zoom = d3.zoom().scaleExtent([0.1, 4]).on("zoom", (event) => {
          svgEl.setAttribute("transform", event.transform.toString());
        });
        (d3.select(containerRef.current) as any).call(zoom);
      }
    }
  }, [svg]);

  const handleResetZoom = () => {
    if (containerRef.current) {
      const zoom = d3.zoom().scaleExtent([0.1, 4]);
      (d3.select(containerRef.current) as any)
        .transition()
        .duration(200)
        .call(zoom.transform, d3.zoomIdentity);
    }
  };

  if (error) return <pre>{error}</pre>;
  if (!svg) return <CircularProgress size={20} />;

  return (
    <div ref={containerRef} className={classes.mermaidWrapper}>
      <div dangerouslySetInnerHTML={{ __html: svg }} />
      <div className={classes.zoomControls}>
        <Tooltip title="Zoom In">
          <IconButton
            className={classes.zoomButton}
            onClick={() => {
              if (containerRef.current) {
                const zoom = d3.zoom().scaleExtent([0.1, 4]);
                (d3.select(containerRef.current) as any).call(zoom.scaleBy, 1.5);
              }
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20">
              <path d="M10 3v14M3 10h14" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out">
          <IconButton
            className={classes.zoomButton}
            onClick={() => {
              if (containerRef.current) {
                const zoom = d3.zoom().scaleExtent([0.1, 4]);
                (d3.select(containerRef.current) as any).call(zoom.scaleBy, 1 / 1.5);
              }
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20">
              <path d="M3 10h14" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset Zoom">
          <IconButton
            className={classes.zoomButton}
            onClick={handleResetZoom}
          >
            <svg width="20" height="20" viewBox="0 0 20 20">
              <path d="M4 10a6 6 0 1 1 1.5 4.1" stroke="currentColor" strokeWidth="2" fill="none" />
              <path d="M4 4v3h3" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

export interface MarkdownPageProps {
  file: string;
  showMermaid?: boolean;
}

export const MarkdownPage: React.FC<MarkdownPageProps> = ({
  file,
  showMermaid = false,
}) => {
  const { classes } = useStyles();
  const [markdown, setMarkdown] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    import(`../docs/${file}`)
      .then((res) => fetch(res.default))
      .then((res) => res.text())
      .then((text) => {
        setMarkdown(text);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [file]);

  const plugins = React.useMemo(() => {
    const rp: any[] = [remarkGfm, [remarktoc, { heading: 'Index', maxDepth: 2 } ]];
    const rh = [raw, slug];
    return { rp, rh };
  }, []);

  const components = {
    // Add the table component here; it will always be available regardless of showMermaid
    table: ({ node, ...props }: any) => (
        <Box
            sx={{
              overflowX: 'auto',
              my: 3,
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
              },
              '& th, & td': {
                padding: '12px 16px',
                borderBottom: '1px solid',
                borderColor: 'divider',
                textAlign: 'left',
              },
              '& th': {
                bgcolor: 'grey.100',
                fontWeight: 600,
              },
              '& tr:nth-of-type(even)': {
                bgcolor: 'action.hover',
              },
            }}
        >
          <table {...props} />
        </Box>
    ),

    // Merge your existing Mermaid logic
    ...(showMermaid && {
      code({ inline, className, children, ...props }: any) {
        const match = /language-(\w+)/.exec(className || "");
        const value = String(children).replace(/\n$/, "");
        if (!inline && match?.[1] === "mermaid") {
          return <MermaidBlock chart={value} />;
        }
        return (
            <code className={className} {...props}>
              {children}
            </code>
        );
      },
    }),
  };

  return (
    <Paper className={classes.table}>
      {loading && <CircularProgress color="primary" />}
      <ReactMarkdown
          children={markdown}
          remarkPlugins={plugins.rp}
          rehypePlugins={plugins.rh}
          components={components}
      />
    </Paper>
  );
};
